import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Building } from '../types/building';
import { BuildingDataService } from '../services/buildingDataService';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface PublicRetrofitCalculatorProps {
  buildings: Building[];
}

const PublicRetrofitCalculator: React.FC<PublicRetrofitCalculatorProps> = ({ buildings }) => {
  const [selectedBorough, setSelectedBorough] = useState<string>('');

  const viableProspects = useMemo(() => 
    BuildingDataService.filterViableProspects(buildings),
    [buildings]
  );

  const filteredBuildings = useMemo(() => {
    if (!selectedBorough) return viableProspects;
    return viableProspects.filter(building => building.borough === selectedBorough);
  }, [viableProspects, selectedBorough]);

  const boroughs = useMemo(() => 
    Array.from(new Set(buildings.map(b => b.borough).filter(Boolean))),
    [buildings]
  );

  // Simplified chart data - only showing basic borough analysis
  const chartData = useMemo(() => {
    const boroughData = boroughs.map(borough => {
      const boroughBuildings = viableProspects.filter(b => b.borough === borough);
      const totalSavings = boroughBuildings.reduce((sum, b) => 
        sum + (b.costBenefitAnalysis?.totalSavingsOverLifetime || 0), 0
      );
      
      return {
        borough,
        count: boroughBuildings.length,
        totalSavings: totalSavings / 1000000, // Convert to millions
      };
    }).filter(data => data.count > 0);

    return boroughData;
  }, [viableProspects, boroughs]);

  const totalPotentialSavings = filteredBuildings.reduce((sum, b) => 
    sum + (b.costBenefitAnalysis?.totalSavingsOverLifetime || 0), 0
  );

  if (buildings.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          NYC Retrofit Calculator - Public Demo
        </h2>
        <p className="text-gray-600">
          Explore retrofit opportunities in NYC buildings. This public version provides basic insights into energy retrofit potential.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          NYC Retrofit Calculator - Public Demo
        </h2>
        <div className="text-sm text-gray-500">
          🌟 <Link 
            to="/internal-retrofit-calculator" 
            className="text-primary-600 hover:text-primary-800 underline"
          >
            Access Full Version
          </Link>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h3 className="text-lg font-medium text-blue-900 mb-2">
          Discover NYC Energy Retrofit Opportunities
        </h3>
        <p className="text-blue-800 text-sm">
          This public calculator provides a preview of retrofit potential across NYC buildings. 
          Get insights into energy savings opportunities in your borough.
        </p>
        
        {/* Usage Instructions */}
        <div className="mt-4 pt-4 border-t border-blue-300">
          <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
            <span className="mr-2">📋</span> How to Use This Tool:
          </h4>
          <ol className="list-decimal list-inside space-y-1 text-sm text-blue-700">
            <li>Select a borough to filter opportunities by location</li>
            <li>Review aggregate savings potential for your area</li>
            <li>Compare retrofit opportunities across different boroughs</li>
            <li>Explore investment requirements and potential returns</li>
            <li>Upgrade to access building-specific analysis</li>
          </ol>
          <p className="text-xs text-blue-600 mt-2">
            💡 <strong>Upgrade Benefits:</strong> Get detailed building-level ROI calculations, specific retrofit recommendations, and direct contractor connections.
          </p>
        </div>
      </div>

      {/* Simple Borough Filter */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Explore by Borough</h3>
        
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedBorough('')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              selectedBorough === '' 
                ? 'bg-primary-600 text-white' 
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            All Boroughs
          </button>
          {boroughs.map(borough => (
            <button
              key={borough}
              onClick={() => setSelectedBorough(borough)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                selectedBorough === borough 
                  ? 'bg-primary-600 text-white' 
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              {borough}
            </button>
          ))}
        </div>
      </div>

      {/* Basic Summary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-primary-50 p-6 rounded-lg">
          <div className="text-3xl font-bold text-primary-600">
            {filteredBuildings.length}
          </div>
          <div className="text-gray-600">
            Retrofit Opportunities
            {selectedBorough && ` in ${selectedBorough}`}
          </div>
        </div>
        
        <div className="bg-success-50 p-6 rounded-lg">
          <div className="text-3xl font-bold text-success-600">
            formatCurrency((totalPotentialSavings / 1000000))
          </div>
          <div className="text-gray-600">
            Potential 10-Year Savings
            {selectedBorough && ` in ${selectedBorough}`}
          </div>
        </div>
      </div>

      {/* Simplified Borough Chart */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Retrofit Potential by Borough
        </h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="borough" />
              <YAxis label={{ value: 'Savings ($M)', angle: -90, position: 'insideLeft' }} />
              <Tooltip 
                formatter={(value: any) => [`$${Number(value).toFixed(1)}M`, 'Potential Savings']}
              />
              <Bar dataKey="totalSavings" fill="#10b981" name="Potential Savings" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Information Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-info-50 border border-info-200 rounded-lg p-6">
          <h4 className="text-lg font-semibold text-info-900 mb-3">
            💡 What is Local Law 97?
          </h4>
          <p className="text-info-800 text-sm">
            NYC's Local Law 97 requires large buildings to reduce carbon emissions. 
            Buildings that don't meet targets face significant fines starting in 2024.
          </p>
        </div>
        
        <div className="bg-warning-50 border border-warning-200 rounded-lg p-6">
          <h4 className="text-lg font-semibold text-warning-900 mb-3">
            🏢 Why Retrofit?
          </h4>
          <p className="text-warning-800 text-sm">
            Energy retrofits help buildings avoid LL97 fines while reducing operating costs. 
            Many retrofits pay for themselves through energy savings.
          </p>
        </div>
      </div>

      {/* Call to Action */}
      <div className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg p-6 text-white">
        <h3 className="text-xl font-bold mb-3">
          Ready for Advanced Analysis?
        </h3>
        <p className="text-primary-100 mb-4">
          Access detailed retrofit cost analysis, ROI calculations, advanced filters, 
          and building-specific recommendations with our internal tools.
        </p>
        <Link 
          to="/internal-retrofit-calculator"
          className="inline-flex items-center px-6 py-3 bg-white text-primary-600 font-medium rounded-md hover:bg-gray-50 transition-colors"
        >
          Access Full Calculator →
        </Link>
      </div>

      {/* Footer Link */}
      <div className="mt-6 text-center">
        <Link 
          to="/internal-retrofit-calculator" 
          className="text-sm text-gray-500 hover:text-primary-600"
        >
          Professional Analysis Tools Available Here
        </Link>
      </div>
    </div>
  );
};

export default PublicRetrofitCalculator;
